/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type RemoteAudioTrack, type Room, Track } from "livekit-client";
import { logger } from "matrix-js-sdk/lib/logger";

/**
 * Сторож потока: замечает, что звук собеседников перестал приходить.
 *
 * Зачем. Путь до сервера может умереть беззвучно: у человека переподключился VPN, и
 * сервер шлёт пакеты на прежний адрес. Сам WebRTC признаёт такой путь мёртвым примерно
 * через пятнадцать секунд (проверка живости), и только тогда начинается восстановление.
 * Замер на стенде 24.09.2026: после смены порта на выходе звук пропадал на 21 секунду.
 *
 * Как. Раз в полсекунды считаем пакеты, принятые по звуку от собеседников. Если счётчик
 * не растёт полторы секунды, а собеседник при этом не заглушен — путь мёртв, и мы сразу
 * зовём восстановление, не дожидаясь, пока это заметит WebRTC.
 *
 * Чего сторож НЕ делает. Он не реагирует на тишину: считаются пакеты, а не громкость, и
 * молчащий собеседник продолжает слать пакеты комфортного шума. Он молчит, если в комнате
 * никто не публикует звук, — иначе разговор вдвоём с выключёнными микрофонами вызывал бы
 * бесконечные перезапуски.
 */

export interface MediaStallWatchdogOptions {
  /** Сколько тишины в счётчиках считаем обрывом */
  readonly stallMs?: number;
  /** Как часто спрашиваем счётчики */
  readonly sampleMs?: number;
  /** Что делать, когда поток встал */
  readonly onStall: (silentMs: number) => void;
}

const DEFAULT_STALL_MS = 1_500;
const DEFAULT_SAMPLE_MS = 500;

/** Сколько пакетов звука приняли от собеседников. null — считать нечего. */
async function receivedAudioPackets(room: Room): Promise<number | null> {
  const tracks: RemoteAudioTrack[] = [];
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.audioTrackPublications.values()) {
      // Заглушённый собеседник пакетов не шлёт — по нему обрыв не определить.
      if (publication.isMuted || !publication.isSubscribed) continue;
      const track = publication.track as RemoteAudioTrack | undefined;
      if (track) tracks.push(track);
    }
  }
  if (tracks.length === 0) return null;

  let total = 0;
  for (const track of tracks) {
    const receiver = track.receiver;
    if (!receiver) continue;
    try {
      const report = await receiver.getStats();
      report.forEach((entry) => {
        if (entry.type === "inbound-rtp" && entry.kind === "audio") {
          total += (entry as RTCInboundRtpStreamStats).packetsReceived ?? 0;
        }
      });
    } catch (error) {
      logger.debug("Сторож потока: счётчики недоступны", error);
    }
  }
  return total;
}

/**
 * Следить за потоком звука. Возвращает функцию остановки.
 */
export function watchMediaStalls(
  room: Room,
  { stallMs = DEFAULT_STALL_MS, sampleMs = DEFAULT_SAMPLE_MS, onStall }: MediaStallWatchdogOptions,
): () => void {
  let lastCount: number | null = null;
  let silentSince: number | null = null;
  let reported = false;
  let stopped = false;

  const tick = async (): Promise<void> => {
    if (stopped) return;
    const count = await receivedAudioPackets(room);
    if (stopped) return;

    if (count === null) {
      // Никто не говорит — считать нечего, начинаем отсчёт заново.
      lastCount = null;
      silentSince = null;
      reported = false;
      return;
    }

    if (lastCount === null || count > lastCount) {
      lastCount = count;
      silentSince = null;
      reported = false;
      return;
    }

    const now = Date.now();
    silentSince ??= now;
    const silentMs = now - silentSince;
    if (!reported && silentMs >= stallMs) {
      reported = true;
      logger.warn(`Сторож потока: звук не приходит ${silentMs} мс, зовём восстановление`);
      onStall(silentMs);
    }
  };

  const timer = setInterval(() => void tick(), sampleMs);
  return (): void => {
    stopped = true;
    clearInterval(timer);
  };
}

/** Нужен для проверок: какой звук сторож считает «живым». */
export const MEDIA_STALL_DEFAULTS = {
  stallMs: DEFAULT_STALL_MS,
  sampleMs: DEFAULT_SAMPLE_MS,
  source: Track.Source.Microphone,
} as const;
